import React from 'react';
import { bindingParts } from '../text';

interface Props {
    value: string;
    values: Record<string, string>;
    onChange: (next: string) => void;
    onFocus?: () => void;
    multiline?: boolean;
    placeholder?: string;
    max?: number;
    as?: keyof JSX.IntrinsicElements;
    className?: string;
    style?: React.CSSProperties;
}

// #region paint
/** Draws the raw text with each {{binding}} as a chip. Only runs while the field isn't focused, so the caret never jumps. */
function paint(el: HTMLElement, value: string, values: Record<string, string>): void {
    el.textContent = '';
    for (const part of bindingParts(value, values)) {
        if (part.kind === 'text') { el.appendChild(document.createTextNode(part.text)); continue; }
        const chip = document.createElement('span');
        chip.className = part.known ? 'wd-chip' : 'wd-chip wd-chip-unknown';
        chip.title = part.known ? `Filled from ${part.key}: “${values[part.key] || '(empty)'}”` : `Unknown binding: ${part.key}`;
        chip.textContent = part.text;
        el.appendChild(chip);
    }
}

// #region readText
function readText(el: HTMLElement, multiline: boolean): string {
    let t = (el.innerText ?? el.textContent ?? '').replace(/\u00a0/g, ' ').replace(/\r\n/g, '\n');
    if (t.endsWith('\n') && !t.endsWith('\n\n')) t = t.slice(0, -1);
    return multiline ? t : t.replace(/\n+/g, ' ');
}

// #region insertPlain
function insertPlain(text: string): void {
    // execCommand keeps the browser's undo stack; it's deprecated but still the only way to do that in contentEditable.
    if (!document.execCommand('insertText', false, text)) {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(text));
        range.collapse(false);
    }
}

// #region EditableText
/**
 * Edit-mode text: a plain-text contentEditable showing the RAW value with {{bindings}} as chips.
 * Every keystroke commits through onChange; Enter only adds a line in multiline fields.
 */
export const EditableText: React.FunctionComponent<Props> = ({ value, values, onChange, onFocus, multiline = false, placeholder, max, as = 'div', className, style }): React.ReactElement => {
    const ref = React.useRef<HTMLElement | null>(null);
    const focused = React.useRef(false);

    React.useLayoutEffect(() => {
        const el = ref.current;
        if (el && !focused.current && readText(el, multiline) !== value) paint(el, value, values);
    });
    // New binding values (a live preview refresh) recolour the chips, but never under the caret.
    React.useLayoutEffect(() => { if (ref.current && !focused.current) paint(ref.current, value, values); }, [values]);

    const commit = (): void => {
        const el = ref.current;
        if (!el) return;
        const next = readText(el, multiline);
        if (next !== value) onChange(next);
    };

    const props = {
        ref: (el: HTMLElement | null) => { ref.current = el; },
        className: `wd-editable${className ? ` ${className}` : ''}${max && value.length > max ? ' wd-over' : ''}`,
        style,
        contentEditable: true,
        suppressContentEditableWarning: true,
        spellCheck: true,
        role: 'textbox',
        'aria-multiline': multiline,
        'aria-label': placeholder,
        'data-placeholder': placeholder ?? '',
        onFocus: () => { focused.current = true; onFocus?.(); },
        onBlur: () => {
            focused.current = false;
            commit();
            if (ref.current) paint(ref.current, readText(ref.current, multiline), values);
        },
        onInput: commit,
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
        onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (multiline) document.execCommand('insertLineBreak');
                else (e.currentTarget as HTMLElement).blur();
            }
            if (e.key === 'Escape') (e.currentTarget as HTMLElement).blur();
        },
        onPaste: (e: React.ClipboardEvent) => {
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            insertPlain(multiline ? text : text.replace(/\s*\n+\s*/g, ' '));
        },
        onDrop: (e: React.DragEvent) => e.preventDefault()
    };
    return React.createElement(as, props);
};
